import React, { useState, useEffect } from 'react';
import { serviceTemplateAPI, vendorAPI, propertyAPI, storeAPI, clientAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Loader2, 
  Eye,
  Plus,
  Trash2,
  Pencil,
  Copy,
  Zap,
  Calendar,
  TrendingUp,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';

const ServiceTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stores, setStores] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createPODialogOpen, setCreatePODialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    vendor_id: '',
    items: [],
    recurrence: '',
    notes: ''
  });
  
  // Current service item being added
  const [currentItem, setCurrentItem] = useState({
    description: '',
    quantity: 1,
    unit: '',
    rate: ''
  });
  
  // Create PO form state
  const [poFormData, setPOFormData] = useState({
    property_id: '',
    store_id: '',
    expected_delivery_date: '',
    client_id: '',
    committed_payment_date: ''
  });

  const [filteredStores, setFilteredStores] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (poFormData.property_id) {
      const filtered = stores.filter(s => s.property_id === poFormData.property_id);
      setFilteredStores(filtered);
      if (filtered.length > 0 && !filtered.find(s => s.id === poFormData.store_id)) {
        setPOFormData(prev => ({ ...prev, store_id: '' }));
      }
    } else {
      setFilteredStores([]);
    }
  }, [poFormData.property_id, stores]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, vendorsRes, propertiesRes, storesRes, clientsRes] = await Promise.all([
        serviceTemplateAPI.getAll(),
        vendorAPI.getAll(),
        propertyAPI.getAll(),
        storeAPI.getAll(),
        clientAPI.getAll()
      ]);
      
      setTemplates(templatesRes.data);
      setVendors(vendorsRes.data);
      setProperties(propertiesRes.data);
      setStores(storesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setSelectedTemplate(template);
      setFormData({
        name: template.name || '',
        description: template.description || '',
        vendor_id: template.vendor_id || '',
        items: template.items || [],
        recurrence: template.recurrence || '',
        notes: template.notes || ''
      });
    } else {
      setSelectedTemplate(null);
      setFormData({
        name: '',
        description: '',
        vendor_id: '',
        items: [],
        recurrence: '',
        notes: ''
      });
    }
    setCurrentItem({ description: '', quantity: 1, unit: '', rate: '' });
    setDialogOpen(true);
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setViewDialogOpen(true);
  };

  const handleOpenCreatePODialog = (template) => {
    setSelectedTemplate(template);
    setPOFormData({
      property_id: '',
      store_id: '',
      expected_delivery_date: '',
      client_id: '',
      committed_payment_date: ''
    });
    setCreatePODialogOpen(true);
  };

  const handleAddItem = () => {
    if (!currentItem.description || !currentItem.rate) {
      toast.error('Please enter service description and rate');
      return;
    }
    
    const amount = parseFloat(currentItem.quantity) * parseFloat(currentItem.rate);
    setFormData({
      ...formData,
      items: [...formData.items, { 
        ...currentItem, 
        quantity: parseFloat(currentItem.quantity),
        rate: parseFloat(currentItem.rate), 
        amount 
      }]
    });
    setCurrentItem({ description: '', quantity: 1, unit: '', rate: '' });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.amount || item.quantity * item.rate), 0);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter template name');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    try {
      setSaving(true);
      
      const dataToSend = {
        ...formData,
        vendor_id: formData.vendor_id || null
      };
      
      if (selectedTemplate) {
        await serviceTemplateAPI.update(selectedTemplate.id, dataToSend);
        toast.success('Template updated successfully');
      } else {
        await serviceTemplateAPI.create(dataToSend);
        toast.success('Template created successfully');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }
    
    try {
      await serviceTemplateAPI.delete(template.id);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleCreatePO = async () => {
    if (!poFormData.property_id || !poFormData.store_id) {
      toast.error('Please select property and store');
      return;
    }

    try {
      setSaving(true);
      const response = await serviceTemplateAPI.createPO(selectedTemplate.id, {
        property_id: poFormData.property_id,
        store_id: poFormData.store_id,
        expected_delivery_date: poFormData.expected_delivery_date || undefined,
        client_id: poFormData.client_id || undefined,
        committed_payment_date: poFormData.committed_payment_date || undefined
      });
      
      toast.success(`Purchase Order ${response.data.po_number} created from template!`);
      setCreatePODialogOpen(false);
      fetchData(); // Refresh to update usage count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO from template');
    } finally {
      setSaving(false);
    }
  };

  const getRecurrenceBadge = (recurrence) => {
    const colors = {
      'monthly': 'bg-blue-100 text-blue-800',
      'quarterly': 'bg-purple-100 text-purple-800',
      'annually': 'bg-green-100 text-green-800',
      'one-time': 'bg-gray-100 text-gray-800'
    };
    return colors[recurrence] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="service-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Service PO Templates
          </h1>
          <p className="text-muted-foreground">Create and manage templates for recurring services</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Templates</p>
                <p className="text-2xl font-bold">{templates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Templates</p>
                <p className="text-2xl font-bold">{templates.filter(t => t.is_active !== false).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total POs Created</p>
                <p className="text-2xl font-bold">{templates.reduce((sum, t) => sum + (t.usage_count || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates List */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No service templates found</p>
              <p className="text-sm mt-2">Create a template to quickly generate Service POs</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Services</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Recurrence</TableHead>
                  <TableHead>Used</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map(template => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{template.name}</span>
                        {template.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {template.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{template.vendor_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{template.items?.length || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(template.total_amount)}
                    </TableCell>
                    <TableCell>
                      {template.recurrence && (
                        <Badge className={getRecurrenceBadge(template.recurrence)}>
                          {template.recurrence}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{template.usage_count || 0}x</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {template.last_used ? formatDate(template.last_used) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleOpenCreatePODialog(template)}
                          title="Create PO from Template"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Zap className="h-4 w-4 mr-1" />
                          Use
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewTemplate(template)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(template)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(template)}
                          title="Delete"
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {selectedTemplate ? 'Edit Service Template' : 'Create Service Template'}
            </DialogTitle>
            <DialogDescription>
              Define a reusable template for recurring service purchases
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Monthly HVAC Maintenance"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Recurrence</Label>
                <Select
                  value={formData.recurrence}
                  onValueChange={(value) => setFormData({ ...formData, recurrence: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                    <SelectItem value="one-time">One-time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Vendor</Label>
                <Select
                  value={formData.vendor_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, vendor_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default vendor</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this template"
                />
              </div>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h4 className="font-medium">Services in Template</h4>
              
              {/* Add Service Form */}
              <div className="grid grid-cols-12 gap-2 p-3 bg-muted rounded-lg">
                <div className="col-span-5">
                  <Input
                    placeholder="Service description"
                    value={currentItem.description}
                    onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    placeholder="Qty"
                    min="0.01"
                    step="0.01"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    placeholder="Unit (hrs, days)"
                    value={currentItem.unit}
                    onChange={(e) => setCurrentItem({ ...currentItem, unit: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    placeholder="Rate"
                    min="0"
                    step="0.01"
                    value={currentItem.rate}
                    onChange={(e) => setCurrentItem({ ...currentItem, rate: e.target.value })}
                  />
                </div>
                <div className="col-span-1">
                  <Button onClick={handleAddItem} size="icon" className="w-full">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Services List */}
              {formData.items.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell>{item.unit || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.rate)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.amount || item.quantity * item.rate)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} className="text-right font-semibold">
                        Template Total:
                      </TableCell>
                      <TableCell className="text-right font-bold text-lg">
                        {formatCurrency(calculateTotal())}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Default Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Default notes to include in POs created from this template..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedTemplate ? 'Update' : 'Create'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Template Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedTemplate?.name}</DialogTitle>
            {selectedTemplate?.description && (
              <DialogDescription>{selectedTemplate.description}</DialogDescription>
            )}
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm p-4 md:p-6 bg-muted rounded-lg">
                <div>
                  <span className="text-muted-foreground">Vendor:</span>
                  <p className="font-medium">{selectedTemplate.vendor_name || 'Not specified'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Recurrence:</span>
                  <p className="font-medium capitalize">{selectedTemplate.recurrence || 'Not specified'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Times Used:</span>
                  <p className="font-medium">{selectedTemplate.usage_count || 0}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Used:</span>
                  <p className="font-medium">
                    {selectedTemplate.last_used ? formatDate(selectedTemplate.last_used) : 'Never'}
                  </p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTemplate.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.description}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell>{item.unit || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.rate)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.amount || item.quantity * item.rate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex justify-end pt-2 border-t">
                <div className="text-right">
                  <span className="text-muted-foreground mr-4">Total:</span>
                  <span className="text-xl font-bold">{formatCurrency(selectedTemplate.total_amount)}</span>
                </div>
              </div>

              {selectedTemplate.notes && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Default Notes:</span>
                  <p className="mt-1">{selectedTemplate.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button 
              onClick={() => {
                setViewDialogOpen(false);
                handleOpenCreatePODialog(selectedTemplate);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <Zap className="h-4 w-4 mr-2" />
              Create PO from Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create PO from Template Dialog */}
      <Dialog open={createPODialogOpen} onOpenChange={setCreatePODialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-lg">Create PO from Template</DialogTitle>
            <DialogDescription>
              Creating Service PO from "{selectedTemplate?.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 md:p-6 bg-muted rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Template Amount:</span>
                <span className="font-bold text-lg">{formatCurrency(selectedTemplate?.total_amount || 0)}</span>
              </div>
              {selectedTemplate?.vendor_name && (
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-muted-foreground">Vendor:</span>
                  <span className="font-medium">{selectedTemplate.vendor_name}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Property *</Label>
              <Select
                value={poFormData.property_id}
                onValueChange={(value) => setPOFormData({ ...poFormData, property_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Store *</Label>
              <Select
                value={poFormData.store_id}
                onValueChange={(value) => setPOFormData({ ...poFormData, store_id: value })}
                disabled={!poFormData.property_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={poFormData.property_id ? "Select store" : "Select property first"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredStores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Expected Delivery
                </Label>
                <Input
                  type="date"
                  value={poFormData.expected_delivery_date}
                  onChange={(e) => setPOFormData({ ...poFormData, expected_delivery_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Payment Due
                </Label>
                <Input
                  type="date"
                  value={poFormData.committed_payment_date}
                  onChange={(e) => setPOFormData({ ...poFormData, committed_payment_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Link to Client (Optional)</Label>
              <Select
                value={poFormData.client_id || "none"}
                onValueChange={(value) => setPOFormData({ ...poFormData, client_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No client</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.client_name} {c.event_type ? `(${c.event_type})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePODialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePO} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Zap className="h-4 w-4 mr-2" />
              Create PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceTemplatesPage;
